import { useState } from "react";
import { toast } from "sonner";
import { X, Loader2, FileText, Clock, Pencil, Save, ExternalLink, Building2 } from "lucide-react";
import * as api from "../../lib/crmApi";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("da-DK", { dateStyle: "short", timeStyle: "short" }) : "—";

export const NOTE_KINDS = [
    { k: "ringt", label: "Ringet", active: "bg-sky-600 text-white border-sky-600" },
    { k: "snakket", label: "Snakket", active: "bg-emerald-600 text-white border-emerald-600" },
    { k: "mail", label: "Mail", active: "bg-violet-600 text-white border-violet-600" },
    { k: "andet", label: "Andet", active: "bg-slate-700 text-white border-slate-700" },
];

const isoToLocalParts = (iso) => {
    if (!iso) return { date: "", time: "09:00" };
    const d = new Date(iso);
    if (isNaN(d)) return { date: "", time: "09:00" };
    const pad = (n) => String(n).padStart(2, "0");
    return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
};

function Btn({ icon: Icon, children, tone, ...rest }) {
    const toneClasses = {
        emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
        violet: "bg-violet-600 hover:bg-violet-700 text-white",
    }[tone] || "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50";
    return (
        <button type="button" {...rest} className={`inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-xs font-semibold disabled:opacity-40 disabled:cursor-not-allowed ${toneClasses}`}>
            {Icon && <Icon className={`h-3.5 w-3.5 ${Icon === Loader2 ? "animate-spin" : ""}`} />} {children}
        </button>
    );
}

/** Viser hele noten + lader brugeren redigere tekst, type og opfølgning.
 *  `lead` (valgfri) viser virksomhed/kontakt og en "Åbn lead"-knap via onOpenLead. */
export default function NoteModal({ activity, leadId, lead, onOpenLead, onClose, onDone }) {
    const p = activity.payload || {};
    const init = isoToLocalParts(p.followup_at);
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(p.text || "");
    const [kind, setKind] = useState(p.kind || "andet");
    const [date, setDate] = useState(init.date);
    const [time, setTime] = useState(init.time);
    const [saving, setSaving] = useState(false);
    const kindLabel = NOTE_KINDS.find((t) => t.k === (p.kind || "andet"))?.label || "Andet";

    const save = async () => {
        if (!text.trim()) return toast.error("Noten må ikke være tom");
        let followup_at = null;
        if (date) {
            const local = new Date(`${date}T${time || "09:00"}:00`);
            followup_at = isNaN(local) ? `${date}T${time || "09:00"}:00` : local.toISOString();
        }
        setSaving(true);
        try {
            await api.updateNote(leadId, activity.id, { text, kind, followup_at });
            toast.success("Note opdateret");
            onDone();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kunne ikke gemme noten");
        } finally { setSaving(false); }
    };

    const reset = () => { setEditing(false); setText(p.text || ""); setKind(p.kind || "andet"); setDate(init.date); setTime(init.time); };

    return (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" onClick={onClose} data-testid="crm-note-modal">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Note · {kindLabel}</div>
                        {lead && (
                            <div className="mt-1 text-sm font-semibold text-slate-900 flex items-center gap-1.5 truncate" data-testid="crm-note-modal-lead">
                                <Building2 className="h-3.5 w-3.5 text-slate-400" /> {lead.company}{lead.contact_name ? <span className="text-slate-500 font-normal"> · {lead.contact_name}</span> : null}
                            </div>
                        )}
                        <div className="text-xs text-slate-500 mt-1">Oprettet {fmtDate(activity.created_at)}{activity.edited_at ? ` · redigeret ${fmtDate(activity.edited_at)}` : ""}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="crm-note-modal-close"><X className="h-5 w-5" /></button>
                </div>

                <div className="px-5 py-4 overflow-y-auto flex-1">
                    {!editing ? (
                        <>
                            <div className="text-sm text-slate-800 whitespace-pre-wrap break-words" data-testid="crm-note-modal-text">{p.text}</div>
                            {p.followup_at && (
                                <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-sky-800 bg-sky-50 border border-sky-200 rounded-lg px-2.5 py-1.5" data-testid="crm-note-modal-followup">
                                    <Clock className="h-3.5 w-3.5" /> Opfølgning {fmtDate(p.followup_at)}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} autoFocus
                                data-testid="crm-note-edit-text"
                                className="w-full rounded-lg border border-slate-300 p-3 text-sm mb-3" />
                            <div className="flex flex-wrap gap-2 mb-3">
                                {NOTE_KINDS.map((t) => (
                                    <button key={t.k} type="button" onClick={() => setKind(t.k)}
                                        data-testid={`crm-note-edit-kind-${t.k}`}
                                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${kind === t.k ? t.active : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>{t.label}</button>
                                ))}
                            </div>
                            <label className="block text-xs font-medium text-slate-600 mb-1">Opfølgning (valgfri)</label>
                            <div className="grid grid-cols-3 gap-2">
                                <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="crm-note-edit-date"
                                    className="col-span-2 rounded-lg border border-slate-300 h-9 px-3 text-sm" />
                                <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="crm-note-edit-time"
                                    className="rounded-lg border border-slate-300 h-9 px-3 text-sm" />
                            </div>
                        </>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
                    {onOpenLead && !editing && (
                        <Btn icon={ExternalLink} onClick={onOpenLead} data-testid="crm-note-open-lead-btn">Åbn lead</Btn>
                    )}
                    <div className="ml-auto flex gap-2">
                        {!editing ? (
                            <>
                                <Btn onClick={onClose}>Luk</Btn>
                                <Btn icon={Pencil} tone="violet" onClick={() => setEditing(true)} data-testid="crm-note-edit-btn">Rediger</Btn>
                            </>
                        ) : (
                            <>
                                <Btn onClick={reset} disabled={saving}>Annullér</Btn>
                                <Btn icon={saving ? Loader2 : Save} tone="emerald" onClick={save} disabled={saving} data-testid="crm-note-save-btn">{saving ? "Gemmer…" : "Gem"}</Btn>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
